import { PlayerEvaluationSchema } from '../types/Evaluation';

/**
 * Creates the form field name key for a given tab and field index.
 * Using a flat key avoids deeply nested form state and improves React Hook Form performance.
 */
export function getFieldKey(tabIndex: number, fieldIndex: number): string {
  return `tab_${tabIndex}_field_${fieldIndex}`;
}

/**
 * Builds default form values from the API schema response.
 * Maps each field to a flat key (tab_{tabIdx}_field_{fieldIdx}) for React Hook Form.
 */
export function buildDefaultValues(schema: PlayerEvaluationSchema): Record<string, any> {
  const defaults: Record<string, any> = {};

  schema.customTabs.forEach((tab, tabIdx) => {
    tab.fields.forEach((field, fieldIdx) => {
      const key = getFieldKey(tabIdx, fieldIdx);
      defaults[key] = field.value;
    });
  });

  return defaults;
}

/**
 * Transforms flat form data back into the API schema structure for submission.
 * Reconstructs the customTabs array with updated field values.
 */
export function transformFormDataForApi(
  formData: Record<string, any>,
  schema: PlayerEvaluationSchema,
): PlayerEvaluationSchema {
  return {
    ...schema,
    customTabs: schema.customTabs.map((tab, tabIdx) => ({
      ...tab,
      fields: tab.fields.map((field, fieldIdx) => {
        const key = getFieldKey(tabIdx, fieldIdx);
        return {
          ...field,
          value: formData[key] !== undefined ? formData[key] : field.value,
        };
      }),
    })),
  };
}

// Transform API response to match the Form Values
export function transformApiResponseToFormValues(
  apiData: PlayerEvaluationSchema,
): PlayerEvaluationSchema {
    const response: PlayerEvaluationSchema = {
        ...apiData,
        customTabs: [{
            name: 'Default',
            fields: [
                { name: 'User Notes', type: 'Text', value: apiData.userNotes },
                { name: 'Account Notes', type: 'MultipleTexts', value: apiData.accountNotes.map(note => note.note) },
                { name: 'Nil Contributions Contributed', type: 'Float', value: apiData.nilContributionsContributed },
                { name: 'Nil Contributions Committed', type: 'Float', value: apiData.nilContributionsCommitted },
                { name: 'Scholarship Status', type: 'Singleselect', value: apiData.scholarshipStatus, options: ['Unknown', 'Eligible', 'Ineligible'] },
                { name: 'Eligibility Remaining', type: 'Singleselect', value: apiData.eligibilityRemaining, options: ['Unknown', '1 Year', '2 Years', '3 Years'] },
                { name: 'Board Card Primary Color', type: 'Color', value: apiData.boardCardPrimaryColor, grid: 6 },
                { name: 'Board Card Secondary Color', type: 'Color', value: apiData.boardCardSecondaryColor, grid: 6 },
            ],
        }, ...apiData.customTabs],
    };
    return response;
}